/**
 * SendScheduleQuestionnaireModal — owner-side flow to send a timing
 * questionnaire to a single contractor. Picks trades + per-trade scope basis
 * (whole_project / per_building / per_unit), then fires the email via the
 * backend route. Returns the public URL so the owner can copy/re-share.
 *
 * Use case: migrating to DrawReady, contracts already in place, just need
 * timing data to populate the schedule.
 *
 * Props:
 *   open, onOpenChange    — dialog control
 *   project               — { id, name, buildings, units_per_building }
 *   user                  — current owner (must have id + email)
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Copy, CheckCircle2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TRADES } from '@/lib/trades';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v2.5.5.12 — default single stage for trades NOT in TRADE_STAGE_MAP. Lets
// the owner ask for timing on Framing / Roofing / Painting etc. with one
// duration input instead of the modal silently hiding them.
const SINGLE_STAGE_DEFAULT = ['Duration'];

// Map our TRADES list (`other`, `windows_doors`, etc.) to lowercased keys
// the backend understands. Skip ``general``, ``permits``, ``demo``, ``other``
// — those aren't real schedulable trades.
const ASK_DAYS_BLOCKLIST = new Set(['general', 'permits', 'demo', 'other']);

export default function SendScheduleQuestionnaireModal({ open, onOpenChange, project, user, initialContractorId, initialTrades }) {
  const [contractors, setContractors] = useState([]);
  const [tradeStageMap, setTradeStageMap] = useState({});
  const [selectedContractorId, setSelectedContractorId] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [selectedTrades, setSelectedTrades] = useState([]); // [{trade, scope_basis}]
  const [noteForContractor, setNoteForContractor] = useState('');
  const [sending, setSending] = useState(false);
  const [sentResult, setSentResult] = useState(null);

  // v2.5.5.10 — when the caller pre-selects (e.g. "Ask for days" on an
  // awarded contractor row), seed the form once we open.
  useEffect(() => {
    if (!open) return;
    if (initialContractorId) setSelectedContractorId(initialContractorId);
    if (Array.isArray(initialTrades) && initialTrades.length > 0) {
      // Default to whole_project; the user can flip per_building in-modal.
      setSelectedTrades(initialTrades.map(t => ({ trade: t, scope_basis: 'whole_project' })));
    }
  }, [open, initialContractorId, initialTrades]);

  useEffect(() => {
    if (!open || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [contractorsR, stagesR] = await Promise.all([
          axios.get(`${API}/contractors?user_id=${user.id}`),
          axios.get(`${API}/trade-stages`),
        ]);
        if (cancelled) return;
        setContractors(contractorsR.data || []);
        setTradeStageMap(stagesR.data?.trade_stages || {});
      } catch (e) {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, [open, user?.id]);

  const buildingCount = useMemo(() => Math.max(1, parseInt(project?.buildings || 1, 10)), [project]);
  const unitsPerBuilding = useMemo(() => Math.max(1, parseInt(project?.units_per_building || 1, 10)), [project]);

  // v2.5.5.12 — Merge the canonical TRADES list with whatever multi-stage
  // trades the backend reports. Single-stage trades get a default 'Duration'
  // stage so the contractor still has something to fill in.
  const tradeOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    // Multi-stage trades from the backend first (canonical names)
    for (const k of Object.keys(tradeStageMap)) {
      if (ASK_DAYS_BLOCKLIST.has(k)) continue;
      const t = TRADES.find(x => x.key === k);
      out.push({ key: k, label: t?.label || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), isMultiStage: true });
      seen.add(k);
    }
    // Then everything else from TRADES that isn't blocked
    for (const t of TRADES) {
      if (seen.has(t.key) || ASK_DAYS_BLOCKLIST.has(t.key)) continue;
      out.push({ key: t.key, label: t.label, isMultiStage: false });
    }
    return out;
  }, [tradeStageMap]);

  const toggleTrade = (trade) => {
    setSelectedTrades(prev => {
      if (prev.find(p => p.trade === trade)) return prev.filter(p => p.trade !== trade);
      const defaultBasis = buildingCount > 1 ? 'per_building' : 'whole_project';
      return [...prev, { trade, scope_basis: defaultBasis }];
    });
  };
  const setBasis = (trade, basis) => {
    setSelectedTrades(prev => prev.map(p => p.trade === trade ? { ...p, scope_basis: basis } : p));
  };

  const handleSend = async () => {
    const isCustom = selectedContractorId === '__custom__';
    if (!isCustom && !selectedContractorId) { toast.error('Pick a contractor first.'); return; }
    if (isCustom && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipientEmail.trim())) {
      toast.error('Enter a valid email address to send to.'); return;
    }
    if (selectedTrades.length === 0) { toast.error('Pick at least one trade.'); return; }
    setSending(true);
    try {
      const payload = {
        user_id: user.id,
        project_id: project.id,
        contractor_id: isCustom ? null : selectedContractorId,
        recipient_email: isCustom ? recipientEmail.trim() : null,
        recipient_name: isCustom ? (recipientName.trim() || null) : null,
        trades: selectedTrades.map(t => ({
          trade: t.trade,
          // v2.5.5.12 — single-stage trades fall back to ['Duration'] so the
          // contractor still has a field to fill out. Multi-stage trades use
          // the canonical breakdown from the backend.
          stages: (tradeStageMap[t.trade] && tradeStageMap[t.trade].length > 0)
            ? tradeStageMap[t.trade]
            : SINGLE_STAGE_DEFAULT,
          scope_basis: t.scope_basis,
        })),
        building_count: buildingCount,
        units_per_building: unitsPerBuilding,
        note_for_contractor: noteForContractor || null,
      };
      const r = await axios.post(`${API}/schedule-questionnaires`, payload);
      setSentResult(r.data);
      toast.success(r.data?.direct_send
        ? `Sent — check ${r.data.contractor_email}.`
        : 'Questionnaire sent. The contractor should have it in their inbox.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send questionnaire.');
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!sentResult?.public_url) return;
    navigator.clipboard.writeText(sentResult.public_url);
    toast.success('Link copied to clipboard.');
  };

  const reset = () => {
    setSentResult(null);
    setSelectedContractorId('');
    setRecipientEmail('');
    setRecipientName('');
    setSelectedTrades([]);
    setNoteForContractor('');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange?.(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-schedule-q-modal">
        <DialogTitle className="text-lg font-bold text-slate-900">
          Send timing questionnaire
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-500 -mt-1">
          {project?.name ? `${project.name} · ` : ''}Pick a contractor and the trades they handle. They&apos;ll get a tokenized link to fill out per-stage days.
        </DialogDescription>

        {sentResult ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="schedule-q-sent-confirm">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-emerald-900">Sent to {sentResult.contractor_email}</div>
                <p className="text-xs text-emerald-700 mt-0.5">
                  {sentResult.direct_send
                    ? 'Delivered straight to that inbox — open the link to experience the contractor form yourself.'
                    : 'In preview the email reroutes to your admin inbox. On production it lands directly with the contractor.'}
                </p>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Public link (re-share if needed)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={sentResult.public_url} readOnly className="text-xs font-mono" data-testid="schedule-q-public-url" />
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="schedule-q-copy-link-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="schedule-q-send-another-btn">Send another</Button>
              <Button size="sm" onClick={() => { reset(); onOpenChange?.(false); }} data-testid="schedule-q-done-btn">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Contractor picker */}
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Send to</Label>
              <select
                value={selectedContractorId}
                onChange={(e) => setSelectedContractorId(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm mt-1"
                data-testid="schedule-q-contractor-select"
              >
                <option value="">Pick a contractor…</option>
                <option value="__custom__">✉️  Send to a specific email (try it yourself)</option>
                {contractors.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.company || c.name} {c.email ? `· ${c.email}` : ''}
                  </option>
                ))}
              </select>
              {selectedContractorId === '__custom__' && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="custom-recipient-block">
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Recipient email</Label>
                    <Input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="you@bank.com"
                      className="mt-1"
                      data-testid="recipient-email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Name (optional)</Label>
                    <Input
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="e.g. Jane at First National"
                      className="mt-1"
                      data-testid="recipient-name-input"
                    />
                  </div>
                  <p className="sm:col-span-2 text-[11px] text-blue-700">
                    We&apos;ll email this address the exact link a contractor receives — open it to fill out the timing form yourself.
                  </p>
                </div>
              )}
            </div>

            {/* Trade picker */}
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Trades they handle on this project
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1.5">
                {tradeOptions.map(trade => {
                  const isSelected = !!selectedTrades.find(p => p.trade === trade.key);
                  return (
                    <button
                      key={trade.key}
                      type="button"
                      onClick={() => toggleTrade(trade.key)}
                      data-testid={`trade-chip-${trade.key}`}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg border text-left flex items-center justify-between gap-1 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <span className="truncate">{trade.label}</span>
                      {trade.isMultiStage && (
                        <span className="text-[8px] uppercase font-bold tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-200 px-1 rounded flex-shrink-0">
                          stages
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                Multi-stage trades (Plumbing, Electrical, HVAC, Drywall, Concrete) show a per-stage breakdown to the contractor. All other trades get a single duration field.
              </p>
            </div>

            {/* Per-trade scope basis — always shown so the owner explicitly
                picks Whole project / Per building / Per unit for each trade.
                The contractor's form then renders day inputs per the choice. */}
            {selectedTrades.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid="scope-basis-block">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  How should they enter days? ({buildingCount} building{buildingCount === 1 ? '' : 's'} · {unitsPerBuilding} unit{unitsPerBuilding === 1 ? '' : 's'} per building)
                </Label>
                {selectedTrades.map(t => (
                  <div key={t.trade} className="flex items-center justify-between gap-2 text-xs">
                    <span className="font-medium text-slate-700 capitalize w-28 flex-shrink-0">{t.trade}</span>
                    <div className="inline-flex bg-white border border-slate-200 rounded-lg p-0.5" data-testid={`scope-toggle-${t.trade}`}>
                      {[
                        { key: 'whole_project', label: 'Whole project' },
                        { key: 'per_building', label: 'Per building' },
                        { key: 'per_unit', label: 'Per unit' },
                      ].map(opt => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setBasis(t.trade, opt.key)}
                          className={`px-2.5 py-1 rounded-md transition ${
                            t.scope_basis === opt.key
                              ? 'bg-blue-600 text-white font-semibold shadow-sm'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                          data-testid={`basis-${t.trade}-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-slate-500">
                  The contractor sees one set of day inputs for <b>Whole project</b>, one set per building for <b>Per building</b>, and one set per unit for <b>Per unit</b>.
                </p>
              </div>
            )}

            {/* Optional note */}
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Note (optional)</Label>
              <Input
                value={noteForContractor}
                onChange={(e) => setNoteForContractor(e.target.value)}
                placeholder="e.g. Just need timing — pricing is locked in already."
                data-testid="schedule-q-note-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="schedule-q-cancel-btn">
                Cancel
              </Button>
              <Button
                disabled={sending || !selectedContractorId || selectedTrades.length === 0}
                onClick={handleSend}
                data-testid="schedule-q-send-btn"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
                Send questionnaire
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
